import React, { useState } from 'react';
import { 
  User, 
  LogOut, 
  ChevronRight,
  Radio,
  MessageCircle,
  Package,
  Search,
  Users,
  Sparkles,
  MapPin,
  Calculator,
  Shield,
  ExternalLink,
  Rocket
} from 'lucide-react';

/**
 * OptionsAdmin Component
 * Admin dashboard main menu with navigation to all admin features
 * Follows Fast Max brand standards with Tailwind CSS
 * 
 * @param {number} userId - Current admin user ID
 * @param {string} username - Admin username for display
 * @param {Function} setActiveComponent - Navigation function to switch components
 * @param {Function} onLogout - Logout handler
 */
function OptionsAdmin({ 
  userId, 
  username, 
  setActiveComponent, 
  onLogout 
}) {
  const [showModal, setShowModal] = useState(false);

  const menuItems = [
    {
      id: 'Accounting',
      label: 'Contabilidad',
      icon: Calculator,
      onClick: () => setActiveComponent('Accounting'),
      color: 'from-[#B1336E] to-[#E6B43C]',
      description: 'Gestión financiera y reportes'
    },
    {
      id: 'ControlTower',
      label: 'Torre de Control',
      icon: Radio,
      onClick: () => setActiveComponent('ControlTower'),
      color: 'from-[#67A43C] to-[#E6B43C]',
      description: 'Monitoreo en tiempo real'
    },
    {
      id: 'Express',
      label: 'Entregas Express',
      icon: Rocket,
      onClick: () => setActiveComponent('ExpressDeliveries'),
      color: 'from-[#67A43C] to-[#E6B43C]',
      description: 'Todas las Entregas Express'
    },
    {
      id: 'Chat',
      label: 'Chat',
      icon: MessageCircle,
      onClick: () => setActiveComponent('Chat'),
      color: 'from-[#B1336E] to-[#67A43C]',
      description: 'Comunicación con usuarios'
    },
    {
      id: 'Inventory',
      label: 'Inventario',
      icon: Package,
      onClick: () => setActiveComponent('PackageDashboard'),
      color: 'from-[#E6B43C] to-[#B1336E]',
      description: 'Control de stock y productos'
    },
    {
      id: 'GeneralSearch',
      label: 'Búsqueda General',
      icon: Search,
      onClick: () => setActiveComponent('SearchGlobal'),
      color: 'from-[#67A43C] to-[#B1336E]',
      description: 'Buscar en todo el sistema'
    },
    {
      id: 'Documentacion',
      label: 'Documentacion',
      icon: Search,
      onClick: () => setActiveComponent('DocsManager'),
      color: 'from-[#67A43C] to-[#B1336E]',
      description: 'Buscar en todo el sistema'
    },
    {
      id: 'Ads',
      label: 'Ads',
      icon: Sparkles,
      onClick: () => setActiveComponent('AdminFeaturedAds'),
      color: 'from-[#67A43C] to-[#B1336E]',
      description: 'Administrar Ads'
    },
    {
      id: 'Drivers',
      label: 'Conductores',
      icon: Users,
      onClick: () => setActiveComponent('Drivers'),
      color: 'from-[#B1336E] to-[#E6B43C]',
      description: 'Gestión de Conductores'
    },
    {
      id: 'Provincia',
      label: 'Provincia',
      icon: MapPin,
      onClick: () => setActiveComponent('ProvinciaDeliveries'),
      color: 'from-[#67A43C] to-[#E6B43C]',
      description: 'Gestión de entregas provincia'
    },
    {
      id: 'Legacy',
      label: 'Legacy',
      icon: MapPin,
      onClick: () => setActiveComponent('MapAdmin'),
      color: 'from-[#67A43C] to-[#E6B43C]',
      description: 'Visita el Antiguo Mapa de guia'
    },
  ];

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo/Brand Section */}
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-sm">
                <Shield className="w-6 h-6 text-brand-primary" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat tracking-tight">
                  FAST MAX
                </h1>
                <p className="text-xs text-white/80 font-montserrat">Panel Administrativo</p>
              </div>
            </div>

            {/* User Info Badge */}
            <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full hidden sm:block">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-white" />
                <span className="text-sm font-medium text-white font-montserrat">
                  {username || 'Admin'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
          
          {/* Welcome Banner */}
          <div className="bg-gradient-to-r from-brand-primary to-pink-600 rounded-2xl shadow-lg p-6 mb-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-extrabold font-montserrat mb-1">
                  ¡Bienvenido, {username || 'Admin'}!
                </h2>
                <p className="text-white/90 font-montserrat text-sm">
                  Gestiona todo el sistema Fast Max desde aquí
                </p>
              </div>
            </div>
          </div>

          {/* Profile Card */}
          <button
            onClick={() => setActiveComponent('MyAccount')}
            className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md hover:border-brand-primary/30 transition-all duration-200 mb-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center shadow-lg flex-shrink-0">
                <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <h2 className="text-lg sm:text-xl font-extrabold text-brand-primary font-montserrat truncate">
                  {username || 'Administrador'}
                </h2>
                <p className="text-sm text-slate-600 font-montserrat">Mi cuenta</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
            </div>
          </button>

          {/* Menu Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={item.onClick}
                  className="group bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
                >
                  <div className="flex flex-col gap-4">
                    {/* Icon and Title Row */}
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-200 flex-shrink-0`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-base sm:text-lg font-semibold text-slate-900 font-montserrat group-hover:text-brand-primary transition-colors duration-200 truncate">
                          {item.label}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-brand-primary group-hover:translate-x-1 transition-all duration-200 flex-shrink-0" />
                    </div>
                    
                    {/* Description */}
                    <p className="text-sm text-slate-500 font-montserrat leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Quick Stats Card (Optional) */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-4">
              Acceso Rápido
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <button
                onClick={() => setActiveComponent('ControlTower')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-brand-green/10 to-brand-green/5 hover:from-brand-green/20 hover:to-brand-green/10 transition-all duration-200 group"
              >
                <Radio className="w-6 h-6 text-brand-green group-hover:scale-110 transition-transform" />
                <span className="text-xs font-semibold text-slate-700 font-montserrat text-center">
                  Torre de Control
                </span>
              </button>

              <button
                onClick={() => setActiveComponent('Accounting')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-brand-primary/10 to-brand-primary/5 hover:from-brand-primary/20 hover:to-brand-primary/10 transition-all duration-200 group"
              >
                <Calculator className="w-6 h-6 text-brand-primary group-hover:scale-110 transition-transform" />
                <span className="text-xs font-semibold text-slate-700 font-montserrat text-center">
                  Contabilidad
                </span>
              </button>

              <button
                onClick={() => setActiveComponent('Drivers')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-brand-gold/10 to-brand-gold/5 hover:from-brand-gold/20 hover:to-brand-gold/10 transition-all duration-200 group"
              >
                <Users className="w-6 h-6 text-brand-gold group-hover:scale-110 transition-transform" />
                <span className="text-xs font-semibold text-slate-700 font-montserrat text-center">
                  Conductores
                </span>
              </button>

              <button
                onClick={() => setActiveComponent('GeneralSearch')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-slate-600/10 to-slate-600/5 hover:from-slate-600/20 hover:to-slate-600/10 transition-all duration-200 group"
              >
                <Search className="w-6 h-6 text-slate-600 group-hover:scale-110 transition-transform" />
                <span className="text-xs font-semibold text-slate-700 font-montserrat text-center">
                  Búsqueda
                </span>
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Fixed Footer with Logout */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={onLogout}
            className="w-full bg-white hover:bg-red-50 border-2 border-red-200 hover:border-red-300 text-red-600 font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sm font-montserrat"
          >
            <LogOut className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default OptionsAdmin;